import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Bell, ChevronRight, Languages, Lock, LogOut, Pencil } from 'lucide-react';

import { AUTH_ROUTE } from '@/constants/routes';
import { APP_VERSION } from '@/constants';
import { useAuthStore } from '@/stores/AuthState';
import { useTranslation } from '@/utils/i18n';
import { removeToken } from '@/stores/secure';
import { updateUser, fetchUserInfo } from '@/api/user';
import { pullRefreshIgnore, usePullToRefresh } from '@/hooks/usePullToRefresh';
import PullToRefreshSpinner from '@/components/PullToRefreshSpinner';
import { toast } from '@/stores/toast';
import { openExternal } from '@/utils/browser';
import { PrimaryButton } from '@/components/Buttons';
import StreamingServiceSelector from '@/components/StreamingServiceSelector';
import NotificationSettingsSheet from '@/components/NotificationSettingsSheet';
import LanguageSheet from '@/components/LanguageSheet';
import { teardownPushNotifications } from '@/services/pushNotifications';
import FeedbackCard from '@/components/FeedbackCard';
import TabBarSpacer from '@/components/navigation/TabBarSpacer';

export const Route = createFileRoute('/_tabs/profile')({
  component: ProfileScreen,
});

const PRIVACY_URL = 'https://info.racoreapp.com/elementor-311/';

function ProfileScreen() {
  const navigate = useNavigate();
  const { t, changeLanguage } = useTranslation();
  const userInfo = useAuthStore((s) => s.userInfo);
  const setUserInfo = useAuthStore((s) => s.setUserInfo);
  const logout = useAuthStore((s) => s.logout);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingName, setEditingName] = useState(false);

  const displayName = userInfo.username || 'Racore';
  const initials =
    displayName
      .replace(/[^\p{L}\p{N}]/gu, '')
      .slice(0, 2)
      .toUpperCase() || 'RA';

  const handleLogout = async () => {
    // Best-effort: deactivate this device's push token server-side while the
    // JWT is still valid (must run BEFORE removeToken).
    await teardownPushNotifications();
    await removeToken();
    await logout();
    navigate({ to: AUTH_ROUTE, replace: true });
  };

  const { pullY, refreshing, handlers } = usePullToRefresh(async () => {
    try {
      const fresh = await fetchUserInfo();
      await setUserInfo(fresh as never);
    } catch {
      toast.error(t('toast.actionFailed'));
    }
  });

  return (
    <div
      className="tw:relative tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:px-4 tw:pb-6"
      data-testid="screen-profile"
      {...handlers}
    >
      <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />
      {/* Header */}
      <div className="tw:flex tw:flex-col tw:items-center tw:pt-6">
        {userInfo.avatar_url ? (
          <img
            src={userInfo.avatar_url}
            alt=""
            className="tw:size-20 tw:rounded-full tw:object-cover"
          />
        ) : (
          <div className="tw:flex tw:size-20 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary">
            <span className="tw:text-[24px] tw:font-semibold tw:text-white">{initials}</span>
          </div>
        )}
        <div className="tw:mt-3 tw:flex tw:items-center tw:gap-2">
          <h1 className="tw:heading-3 tw:text-white">{displayName}</h1>
          <button
            type="button"
            onClick={() => setEditingName(true)}
            aria-label={t('profile.editUsername')}
            data-testid="btn-edit-username"
            className="tw:text-grey-3"
          >
            <Pencil size={16} />
          </button>
        </div>
        {userInfo.email && <p className="tw:caption tw:text-grey-3">{userInfo.email}</p>}
      </div>

      {/* Favorite streaming service */}
      <StreamingServiceSelector className="tw:mt-6 tw:rounded-2xl tw:bg-dark-1 tw:p-5" />

      {/* Préférences */}
      <SectionTitle>{t('profile.preferencesSection')}</SectionTitle>
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Row
          icon={<Bell size={18} />}
          label={t('profile.notifications.title')}
          right={<ChevronRight size={18} className="tw:text-grey-3" />}
          onClick={() => setShowNotifications(true)}
          testid="row-notifications"
        />
        <Row
          icon={<Languages size={18} />}
          label={t('profile.language')}
          right={<ChevronRight size={18} className="tw:text-grey-3" />}
          onClick={() => setShowLanguage(true)}
          testid="row-language"
        />
      </div>

      {/* Aide-nous à améliorer Racore — brings its own section title */}
      <FeedbackCard trigger="profile" />

      {/* Compte */}
      <SectionTitle>{t('profile.accountSection')}</SectionTitle>
      <Row
        icon={<Lock size={18} />}
        label={t('profile.privacy')}
        right={<ChevronRight size={18} className="tw:text-grey-3" />}
        onClick={() => void openExternal(PRIVACY_URL)}
        testid="row-privacy"
      />

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        data-testid="btn-logout"
        className="tw:mt-8 tw:flex tw:h-16 tw:shrink-0 tw:items-center tw:justify-center tw:gap-2 tw:rounded-2xl tw:border tw:border-error/40 tw:bg-error/10 tw:text-base tw:font-semibold tw:text-error"
      >
        <LogOut size={18} />
        {t('profile.logout')}
      </button>

      <p className="tw:mt-4 tw:text-center tw:legals tw:text-grey-4">App version: {APP_VERSION}</p>

      {/* language-sheet — a bottom sheet like the notification settings, not a
          list unfolding under the row. */}
      <LanguageSheet
        open={showLanguage}
        current={userInfo.language}
        onPick={(language) => void changeLanguage(language)}
        onClose={() => setShowLanguage(false)}
      />

      <NotificationSettingsSheet
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {editingName && (
        <EditUsernameModal
          current={userInfo.username ?? ''}
          onClose={() => setEditingName(false)}
          onSaved={async (user) => {
            await setUserInfo(user as never);
            setEditingName(false);
          }}
        />
      )}
      <TabBarSpacer />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="tw:mb-3 tw:mt-7 tw:heading-3 tw:text-white">{children}</h2>;
}

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-white">
      {children}
    </span>
  );
}

interface RowProps {
  icon: React.ReactNode;
  label: string;
  right?: React.ReactNode;
  onClick?: () => void;
  testid?: string;
}

function Row({ icon, label, right, onClick, testid }: RowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="tw:flex tw:h-16 tw:w-full tw:shrink-0 tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-1 tw:px-3 tw:text-base tw:text-white"
    >
      <IconCircle>{icon}</IconCircle>
      <span className="tw:flex-1 tw:text-left">{label}</span>
      {right}
    </button>
  );
}

function EditUsernameModal({
  current,
  onClose,
  onSaved,
}: {
  current: string;
  onClose: () => void;
  onSaved: (user: unknown) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(current);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (username: string) => {
      await updateUser({ username });
      return fetchUserInfo();
    },
    onSuccess: (user) => {
      toast.success(t('toast.usernameUpdated'));
      void onSaved(user);
    },
    onError: () => {
      setError(t('profile.error.usernameUpdateFailed'));
      toast.error(t('profile.error.usernameUpdateFailed'));
    },
  });

  return (
    <div
      className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:bg-black/60 tw:px-6"
      role="dialog"
      aria-modal="true"
      data-testid="modal-edit-username"
      {...pullRefreshIgnore}
    >
      <div className="tw:w-full tw:max-w-[340px] tw:rounded-2xl tw:bg-dark-1 tw:p-5">
        <h3 className="tw:mb-3 tw:heading-3 tw:text-white">{t('profile.editUsername')}</h3>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setError('');
            setValue(e.target.value);
          }}
          maxLength={40}
          placeholder={t('profile.usernamePlaceholder')}
          data-testid="input-username"
          className="tw:h-12 tw:w-full tw:rounded-xl tw:border tw:border-blue-2/40 tw:bg-dark-primary tw:px-4 tw:text-base tw:text-white tw:placeholder:text-grey-4"
        />
        {error && <p className="tw:mt-2 tw:text-[12px] tw:text-error">{error}</p>}
        <div className="tw:mt-4 tw:flex tw:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="tw:h-12 tw:flex-1 tw:rounded-xl tw:bg-dark-primary tw:text-base tw:text-white"
          >
            {t('profile.cancel')}
          </button>
          <div className="tw:flex-1">
            <PrimaryButton
              label={t('profile.save')}
              onClick={() => value.trim() && mutation.mutate(value.trim())}
              loading={mutation.isPending}
              data-testid="btn-save-username"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
